import { useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams, useNavigate } from 'react-router-dom';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { useEvents } from '../lib/useEvents.js';
import { STATUS_BUCKETS, PublicationRollup, formatDateShort, DriftDot, RowActions } from '../lib/events.jsx';
import { LoadingState, ErrorState, Spinner } from '../lib/Spinner.jsx';
import EventDrawer from './EventDrawer.jsx';

const COLUMNS = [
  { key: 'draft', label: 'Draft' },
  ...STATUS_BUCKETS,
];

function KanbanThumb({ url, talent }) {
  if (url) {
    return <img src={url} alt="" loading="lazy" className="w-10 h-10 rounded object-cover bg-muted flex-shrink-0" />;
  }
  const initials = (talent || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join('') || '?';
  const hue = Math.abs((talent || '').split('').reduce((a, c) => a * 31 + c.charCodeAt(0), 7)) % 360;
  return (
    <div
      className="w-10 h-10 rounded flex-shrink-0 flex items-center justify-center text-xs font-semibold text-white"
      style={{ background: `hsl(${hue} 55% 45%)` }}
    >{initials}</div>
  );
}

function CardBody({ event, dragging, pending, cardError, tenantSlug, onActionComplete }) {
  return (
    <div className={`relative bg-background border rounded-md p-2.5 text-sm space-y-1 transition-colors cursor-pointer ${dragging ? 'border-primary shadow-lg opacity-90' : 'border-border hover:border-primary/50'} ${pending ? 'opacity-60' : ''}`}>
      {pending && (
        <div className="absolute top-1.5 right-1.5 flex items-center gap-1 text-[10px] text-muted-foreground bg-background/80 rounded px-1">
          <Spinner size={10} /> moving…
        </div>
      )}
      {cardError && (
        <div className="text-[10px] text-destructive border border-destructive/40 bg-destructive/10 rounded px-1.5 py-0.5" title={cardError}>
          ⚠ move failed: {cardError}
        </div>
      )}
      <div className="flex items-start gap-2">
        <KanbanThumb url={event.flyer_image_url} talent={event.talent} />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate flex items-center gap-1.5">
            <span className="truncate">{event.talent}</span>
            <DriftDot event={event} size={6} />
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <span>{formatDateShort(event.date)}</span>
            {event.day_of_week && <span>· {event.day_of_week}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between pt-1">
        <PublicationRollup publications={event.publications} />
        {event.ref_code && <span className="text-xs text-muted-foreground">?ref={event.ref_code}</span>}
      </div>
      {tenantSlug && (
        <div className="pt-1 flex justify-end" onPointerDown={(ev) => ev.stopPropagation()}>
          <RowActions event={event} tenantSlug={tenantSlug} onActionComplete={onActionComplete} />
        </div>
      )}
    </div>
  );
}

function DraggableCard({ event, onOpen, pending, cardError, tenantSlug, onActionComplete }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: event.id });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} onDoubleClick={onOpen}>
      <CardBody event={event} dragging={isDragging} pending={pending} cardError={cardError} tenantSlug={tenantSlug} onActionComplete={onActionComplete} />
    </div>
  );
}

function DroppableColumn({ column, events, onOpen, optimistic, moveError, tenantSlug, onActionComplete }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  return (
    <div ref={setNodeRef} className={`bg-muted/30 rounded-md p-2 flex flex-col min-h-[400px] transition-colors ${isOver ? 'bg-primary/10 ring-2 ring-primary/30' : ''}`}>
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-sm font-medium">{column.label}</div>
        <div className="text-xs text-muted-foreground">{events.length}</div>
      </div>
      <div className="space-y-2 overflow-y-auto flex-1">
        {events.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-4 italic">No events</div>
        ) : (
          events.map((e) => (
            <DraggableCard
              key={e.id} event={e} onOpen={() => onOpen(e.id)}
              pending={!!optimistic[e.id]}
              cardError={moveError?.id === e.id ? moveError.message : null}
              tenantSlug={tenantSlug} onActionComplete={onActionComplete}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function EventsKanban() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const { events, count, loading, error, refresh } = useEvents(tenantSlug);
  const [optimistic, setOptimistic] = useState({}); // eventId → newStatus
  const [activeId, setActiveId] = useState(null);
  const [moveError, setMoveError] = useState(null);
  const [drawerEvent, setDrawerEvent] = useState(null);
  const eventById = useMemo(() => Object.fromEntries(events.map((e) => [e.id, e])), [events]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const buckets = useMemo(() => {
    const map = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    for (const e of events) {
      const status = optimistic[e.id] || e.status;
      (map[status] || map.draft).push(e);
    }
    return map;
  }, [events, optimistic]);

  const handleDragEnd = async (evt) => {
    setActiveId(null);
    if (!evt.over) return;
    const eventId = evt.active.id;
    const newStatus = evt.over.id;
    const event = events.find((e) => e.id === eventId);
    if (!event || event.status === newStatus) return;
    setOptimistic((o) => ({ ...o, [eventId]: newStatus }));
    setMoveError(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || d.error);
      }
      setOptimistic((o) => { const c = { ...o }; delete c[eventId]; return c; });
      refresh();
    } catch (e) {
      setMoveError({ id: eventId, message: e.message });
      setOptimistic((o) => { const c = { ...o }; delete c[eventId]; return c; });
    }
  };

  if (loading) return <LoadingState label="Loading board…" />;
  if (error) return <ErrorState error={error} onRetry={refresh} />;
  const activeEvent = activeId ? events.find((e) => e.id === activeId) : null;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Kanban</h1>
          <div className="text-xs text-muted-foreground">drag cards to change status · double-click to open</div>
        </div>
        <div className="text-sm text-muted-foreground">
          {count} events
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 flex-1 min-h-0">
          {COLUMNS.map((col) => (
            <DroppableColumn
              key={col.key}
              column={col}
              events={buckets[col.key]}
              onOpen={(id) => setDrawerEvent(eventById[id])}
              optimistic={optimistic}
              moveError={moveError}
              tenantSlug={tenantSlug}
              onActionComplete={refresh}
            />
          ))}
        </div>
        <DragOverlay>{activeEvent && <CardBody event={activeEvent} dragging />}</DragOverlay>
      </DndContext>
      {drawerEvent && (
        <EventDrawer
          event={drawerEvent}
          tenantSlug={tenantSlug}
          onClose={() => setDrawerEvent(null)}
          onAction={refresh}
        />
      )}
    </div>
  );
}
